// RFC 7396 JSON Merge Patch: generate a patch between two values, and apply one.

/** The result of diffing two values. */
export interface Diff {
	/** A merge patch transforming the old value into the new one. */
	patch: unknown;

	/**
	 * Set when the change can't be faithfully expressed as a merge patch, so the caller should
	 * publish a full snapshot instead. This happens when a value is set to null, or an object
	 * holding a null is written whole, since merge patch reads a null as a key deletion. Arrays are
	 * fine: merge patch replaces them wholesale, which is still typically smaller than a full
	 * snapshot.
	 */
	forcedSnapshot: boolean;
}

type Obj = Record<string, unknown>;

function isObject(value: unknown): value is Obj {
	return typeof value === "object" && value !== null && !Array.isArray(value);
}

// Assignment to __proto__ invokes an inherited setter; JSON requires an own data field.
function set(object: Obj, key: string, value: unknown): void {
	if (key === "__proto__") {
		Object.defineProperty(object, key, { value, enumerable: true, configurable: true, writable: true });
	} else {
		object[key] = value;
	}
}

/**
 * Generate an RFC 7396 merge patch transforming `oldVal` into `newVal`.
 *
 * Only object roots produce a recursive patch; any other root forces a snapshot.
 */
export function diff(oldVal: unknown, newVal: unknown): Diff {
	if (isObject(oldVal) && isObject(newVal)) {
		const patch: Obj = {};
		const forced = { value: false };
		diffObjects(oldVal, newVal, patch, forced);
		return { patch, forcedSnapshot: forced.value };
	}
	return { patch: newVal, forcedSnapshot: true };
}

function diffObjects(oldObj: Obj, newObj: Obj, patch: Obj, forced: { value: boolean }): void {
	// Keys present in old but missing from new become explicit null deletions.
	for (const key of Object.keys(oldObj)) {
		if (!Object.hasOwn(newObj, key)) set(patch, key, null);
	}

	for (const key of Object.keys(newObj)) {
		const newV = newObj[key];
		const inOld = Object.hasOwn(oldObj, key);
		const oldV = inOld ? oldObj[key] : undefined;

		if (inOld && deepEqual(oldV, newV)) continue;

		// Recurse into nested objects so unchanged sibling keys stay out of the patch.
		if (isObject(oldV) && isObject(newV)) {
			const sub: Obj = {};
			diffObjects(oldV, newV, sub, forced);
			if (Object.keys(sub).length > 0) set(patch, key, sub);
			continue;
		}

		// Added, or replaced by a value of another type, so it's written whole. A literal null can't be
		// stored, not even nested in an object: merge patch would delete the key. Arrays are kept in the
		// patch and replace the target wholesale.
		if (newV === null || hasNullMember(newV)) {
			forced.value = true;
		}
		set(patch, key, newV);
	}
}

// Whether an object holds a null member at any depth. Nulls inside arrays are data, since merge patch
// replaces an array wholesale.
function hasNullMember(value: unknown): boolean {
	return isObject(value) && Object.values(value).some((v) => v === null || hasNullMember(v));
}

/**
 * Apply an RFC 7396 merge patch to a target value, returning the result.
 *
 * Objects merge recursively; a null patch value deletes that key; any other patch value
 * (scalar or array) replaces the target wholesale.
 */
export function merge(target: unknown, patch: unknown): unknown {
	if (!isObject(patch)) return patch;

	const base: Obj = isObject(target) ? { ...target } : {};
	for (const [key, value] of Object.entries(patch)) {
		if (value === null) {
			delete base[key];
		} else {
			set(base, key, merge(Object.hasOwn(base, key) ? base[key] : undefined, value));
		}
	}
	return base;
}

/** Structural equality for JSON values. */
export function deepEqual(a: unknown, b: unknown): boolean {
	if (a === b) return true;

	if (Array.isArray(a) && Array.isArray(b)) {
		return a.length === b.length && a.every((item, i) => deepEqual(item, b[i]));
	}

	if (isObject(a) && isObject(b)) {
		const keys = Object.keys(a);
		if (keys.length !== Object.keys(b).length) return false;
		return keys.every((key) => Object.hasOwn(b, key) && deepEqual(a[key], b[key]));
	}

	return false;
}
